import { z } from 'zod';
import {
  DEFAULT_PRESENTATION,
  interactionContentSchema,
  type Format,
  type InteractionContent,
  type Presentation,
  type QuestionType,
} from './content';

/**
 * What each format is, expressed as constraints on the one content model.
 *
 * This table is the whole of "one engine, many formats". Adding a rating or a prediction format
 * means adding a row here and teaching the renderer one new layout, not building a subsystem.
 */
export interface FormatRules {
  readonly label: string;
  readonly minQuestions: number;
  readonly maxQuestions: number;
  readonly questionTypes: readonly QuestionType[];
  /** Every question must mark at least one option correct. */
  readonly requiresCorrectAnswers: boolean;
  /** Options carry weights towards declared outcomes. */
  readonly requiresOutcomes: boolean;
  /** Whether a reader is ever shown aggregate or scored results. */
  readonly hasResults: boolean;
}

export const FORMAT_RULES: Readonly<Record<Format, FormatRules>> = Object.freeze({
  poll: {
    label: 'Poll',
    minQuestions: 1,
    maxQuestions: 1,
    questionTypes: ['single_choice'],
    requiresCorrectAnswers: false,
    requiresOutcomes: false,
    hasResults: true,
  },
  trivia_quiz: {
    label: 'Trivia quiz',
    minQuestions: 1,
    maxQuestions: 50,
    questionTypes: ['single_choice', 'multiple_choice'],
    requiresCorrectAnswers: true,
    requiresOutcomes: false,
    hasResults: true,
  },
  personality_quiz: {
    label: 'Personality quiz',
    minQuestions: 1,
    maxQuestions: 50,
    questionTypes: ['single_choice', 'multiple_choice'],
    requiresCorrectAnswers: false,
    requiresOutcomes: true,
    hasResults: true,
  },
  survey: {
    label: 'Survey',
    minQuestions: 1,
    maxQuestions: 100,
    questionTypes: ['single_choice', 'multiple_choice', 'free_text', 'scale'],
    requiresCorrectAnswers: false,
    requiresOutcomes: false,
    hasResults: false,
  },
  form: {
    label: 'Form',
    minQuestions: 1,
    maxQuestions: 100,
    questionTypes: ['free_text', 'single_choice', 'multiple_choice'],
    requiresCorrectAnswers: false,
    requiresOutcomes: false,
    hasResults: false,
  },
});

/**
 * Exported so the published document can be held to the same rules as the draft.
 *
 * A published version that breaks its own format is not a theoretical problem: it is what a reader
 * would actually be served, and the widget would have to guess what to do with it.
 */
export function checkFormatRules(content: InteractionContent, ctx: z.RefinementCtx): void {
  const rules = FORMAT_RULES[content.format];

  if (content.questions.length < rules.minQuestions) {
    ctx.addIssue({
      code: 'custom',
      message: `a ${rules.label.toLowerCase()} needs at least ${rules.minQuestions} question`,
      path: ['questions'],
    });
  }
  if (content.questions.length > rules.maxQuestions) {
    ctx.addIssue({
      code: 'custom',
      message: `a ${rules.label.toLowerCase()} takes at most ${rules.maxQuestions} question(s)`,
      path: ['questions'],
    });
  }

  const outcomeIds = new Set((content.outcomes ?? []).map((outcome) => outcome.id));

  if (rules.requiresOutcomes && outcomeIds.size < 2) {
    ctx.addIssue({
      code: 'custom',
      message: 'a personality quiz needs at least two outcomes to choose between',
      path: ['outcomes'],
    });
  }
  if (!rules.requiresOutcomes && outcomeIds.size > 0) {
    ctx.addIssue({
      code: 'custom',
      message: `a ${rules.label.toLowerCase()} has no outcomes`,
      path: ['outcomes'],
    });
  }

  // Only when the interaction sets its own presentation. One that inherits its brand's theme has
  // `show_results` decided by `resolvePresentation` from the format itself, so it cannot be wrong.
  if (!rules.hasResults && content.presentation?.show_results !== undefined && content.presentation.show_results !== 'never') {
    ctx.addIssue({
      code: 'custom',
      message: `a ${rules.label.toLowerCase()} does not show results to the reader`,
      path: ['presentation', 'show_results'],
    });
  }

  const seenQuestionIds = new Set<string>();
  const seenOptionIds = new Set<string>();

  content.questions.forEach((question, index) => {
    const at = ['questions', index] as const;

    if (seenQuestionIds.has(question.id)) {
      ctx.addIssue({ code: 'custom', message: 'duplicate question id', path: [...at, 'id'] });
    }
    seenQuestionIds.add(question.id);

    if (!rules.questionTypes.includes(question.type)) {
      ctx.addIssue({
        code: 'custom',
        message: `a ${rules.label.toLowerCase()} cannot use a ${question.type} question`,
        path: [...at, 'type'],
      });
    }

    const choiceQuestion = question.type === 'single_choice' || question.type === 'multiple_choice';

    if (choiceQuestion && question.options.length < 2) {
      ctx.addIssue({
        code: 'custom',
        message: 'a choice question needs at least two options',
        path: [...at, 'options'],
      });
    }
    if (!choiceQuestion && question.options.length > 0) {
      ctx.addIssue({
        code: 'custom',
        message: `a ${question.type} question carries no options`,
        path: [...at, 'options'],
      });
    }
    if (question.multiline !== undefined && question.type !== 'free_text') {
      ctx.addIssue({
        code: 'custom',
        message: 'only a free text question can be one line or a paragraph',
        path: [...at, 'multiline'],
      });
    }

    if (question.type === 'scale' && question.scale === undefined) {
      ctx.addIssue({
        code: 'custom',
        message: 'a scale question needs a scale range',
        path: [...at, 'scale'],
      });
    }
    if (question.scale !== undefined && question.scale.min >= question.scale.max) {
      ctx.addIssue({
        code: 'custom',
        message: 'scale min must be below scale max',
        path: [...at, 'scale'],
      });
    }

    if (rules.requiresCorrectAnswers && choiceQuestion) {
      const correct = question.options.filter((option) => option.is_correct === true).length;
      if (correct === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'a trivia question needs a correct answer',
          path: [...at, 'options'],
        });
      }
      if (question.type === 'single_choice' && correct > 1) {
        ctx.addIssue({
          code: 'custom',
          message: 'a single choice trivia question can only have one correct answer',
          path: [...at, 'options'],
        });
      }
    }

    question.options.forEach((option, optionIndex) => {
      const optionAt = [...at, 'options', optionIndex] as const;

      if (seenOptionIds.has(option.id)) {
        ctx.addIssue({ code: 'custom', message: 'duplicate option id', path: [...optionAt, 'id'] });
      }
      seenOptionIds.add(option.id);

      if (!rules.requiresCorrectAnswers && option.is_correct !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: `a ${rules.label.toLowerCase()} has no correct answers`,
          path: [...optionAt, 'is_correct'],
        });
      }

      if (rules.requiresOutcomes) {
        const weights = option.outcome_weights;
        if (weights === undefined || Object.keys(weights).length === 0) {
          ctx.addIssue({
            code: 'custom',
            message: 'every option in a personality quiz must point at an outcome',
            path: [...optionAt, 'outcome_weights'],
          });
        } else {
          for (const outcomeId of Object.keys(weights)) {
            if (!outcomeIds.has(outcomeId)) {
              ctx.addIssue({
                code: 'custom',
                message: `weight refers to ${outcomeId}, which is not a declared outcome`,
                path: [...optionAt, 'outcome_weights'],
              });
            }
          }
        }
      } else if (option.outcome_weights !== undefined) {
        ctx.addIssue({
          code: 'custom',
          message: `a ${rules.label.toLowerCase()} has no outcomes to weight`,
          path: [...optionAt, 'outcome_weights'],
        });
      }
    });
  });
}

/**
 * The full check: shape first, then the constraints the chosen format puts on that shape.
 *
 * Nothing is published without passing this, and the editor runs the identical schema, so an
 * interaction that saves is an interaction that renders.
 */
export const interactionSchema = interactionContentSchema.superRefine(checkFormatRules);

/**
 * The presentation a given format should start with.
 *
 * A survey and a form show a reader no results, and `show_results` anything other than 'never' fails
 * validation for them. Deriving it from the format removes a trap that every caller would otherwise
 * have to remember, and there was already one caller getting it right by hand.
 */
export function defaultPresentation(format: Format): Presentation {
  return {
    ...DEFAULT_PRESENTATION,
    show_results: FORMAT_RULES[format].hasResults ? DEFAULT_PRESENTATION.show_results : 'never',
  };
}
